/**
 * Mis facturas (P30) · listado de acuses de validación del solicitante actual
 * + descargas (XML original y PDF del acuse, generado en runtime).
 */
import { axiosClient } from "@/shared/api/axios-client";
import { encolarDescarga } from "@/shared/api/descarga-secuencial";

export interface Factura {
  id: string;
  solicitudId: string;
  numeroSolicitud: number;
  uuid: string;
  serie: string | null;
  folio: string | null;
  rfcEmisor: string;
  nombreEmisor: string;
  rfcReceptor: string;
  nombreReceptor: string;
  fechaValidacion: string;
  fechaTimbrado: string | null;
  fechaEmision: string | null;
  total: number;
  moneda: string;
  tipoComprobante: string;
  estatus: string;
  ordenCompraClave: string | null;
  observaciones: string | null;
}

/**
 * Query params del listado paginado · TODO el filtrado/orden/paginado es
 * server-side. Las fechas son días "YYYY-MM-DD" (Del=Al para filtrar un día);
 * los demás filtros son contains normalizado por columna.
 */
export interface FacturasParams {
  timbradoDel?: string;
  timbradoAl?: string;
  emitidoDel?: string;
  emitidoAl?: string;
  validadoDel?: string;
  validadoAl?: string;
  uuid?: string;
  emisor?: string;
  receptor?: string;
  estatus?: string;
  serie?: string;
  folio?: string;
  ordenCompra?: string;
  tipo?: string;
  total?: string;
  moneda?: string;
  observaciones?: string;
  numDoc?: string;
  sortKey?: string;
  sortDir?: string;
  page?: number;
  pageSize?: number;
}

/** Página del listado · espejo de FacturasPageDto del backend. */
export interface FacturasPage {
  items: Factura[];
  total: number;
  page: number;
  pageSize: number;
}

function buildQueryString(filtros: FacturasParams): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filtros)) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export async function listarMisFacturas(params: FacturasParams = {}): Promise<FacturasPage> {
  const { data } = await axiosClient.get<FacturasPage>(
    `/api/facturas${buildQueryString(params)}`
  );
  return data;
}

/**
 * Descarga vía blob · el filename real viene en `Content-Disposition`
 * (mismo patrón que las descargas CSV de auditoría).
 *
 * SECUENCIAL por directiva: las descargas van por la cola global — de una
 * en una, sin paralelas ni ráfagas ni pausas (ver descarga-secuencial.ts).
 */
async function descargarBlob(
  url: string,
  contentType: string,
  fallbackFilename: string
): Promise<void> {
  const response = await encolarDescarga(() =>
    axiosClient.get<Blob>(url, { responseType: "blob" })
  );
  const disposition = String(response.headers["content-disposition"] ?? "");
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;
  const blob = new Blob([response.data], { type: contentType });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export async function descargarXmlDeFactura(f: Factura): Promise<void> {
  await descargarBlob(`/api/facturas/${f.id}/xml`, "text/xml", `${f.uuid}.xml`);
}

export async function descargarAcusePdf(f: Factura): Promise<void> {
  await descargarBlob(
    `/api/facturas/${f.id}/acuse.pdf`,
    "application/pdf",
    `acuse_${f.numeroSolicitud}_${f.uuid}.pdf`
  );
}
